import { useState, useEffect } from "react";
import { Mail, Phone, MapPin, Clock, Send, CheckCircle } from "lucide-react";
import { useIsMobile } from "../hooks/useIsMobile";
import { PageWrapper, DarkNav, DarkFooter } from "../components/PageShell";

const CONTACT_CSS = `
  @keyframes slideInLeft {
    from { opacity:0; transform:translateX(-36px); }
    to   { opacity:1; transform:translateX(0); }
  }
  @keyframes slideInRight {
    from { opacity:0; transform:translateX(36px); }
    to   { opacity:1; transform:translateX(0); }
  }
  @keyframes cyanPulse {
    0%,100% { box-shadow:0 0 0 0 rgba(6,182,212,0); }
    50%      { box-shadow:0 0 0 8px rgba(6,182,212,0.08); }
  }
  @keyframes sentCheck {
    from { opacity:0; transform:scale(0.6); }
    to   { opacity:1; transform:scale(1); }
  }
  .contact-left  { animation: slideInLeft  0.65s 0.1s cubic-bezier(.23,1,.32,1) both; }
  .contact-right { animation: slideInRight 0.65s 0.2s cubic-bezier(.23,1,.32,1) both; }
  .contact-card  { transition: transform 0.2s, box-shadow 0.2s; }
  .contact-card:hover { transform:translateY(-2px); box-shadow:0 12px 32px rgba(6,182,212,0.15); }

  .lk-float-field { position:relative; }
  .lk-float-label {
    position:absolute; left:15px; top:50%; transform:translateY(-50%);
    font-size:13px; color:rgba(255,255,255,0.38); pointer-events:none;
    transition:all 0.2s cubic-bezier(.23,1,.32,1);
  }
  .lk-float-label.active {
    top:8px; transform:translateY(0); font-size:10px; color:#06B6D4; letter-spacing:0.5px;
  }
  .lk-float-label.textarea { top:16px; transform:none; }
  .lk-float-label.textarea.active { top:8px; font-size:10px; color:#06B6D4; }
`;

function FloatInput({ label, name, type="text", value, focused, onChange, onFocus, onBlur }) {
  const isActive = focused || value;
  return (
    <div className="lk-float-field" style={{ marginBottom:14 }}>
      <label className={`lk-float-label${isActive?" active":""}`}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(name, e.target.value)}
        onFocus={() => onFocus(name)}
        onBlur={() => onBlur(name)}
        style={{
          width:"100%", boxSizing:"border-box",
          background:"rgba(255,255,255,0.05)",
          border:`1px solid ${focused ? "rgba(6,182,212,0.6)" : "rgba(255,255,255,0.1)"}`,
          borderRadius:11, padding: isActive ? "22px 15px 8px" : "15px 15px",
          fontSize:14, color:"#fff", outline:"none",
          transition:"border-color 0.2s, padding 0.2s",
          fontFamily:"inherit",
        }}
      />
    </div>
  );
}

function FloatTextarea({ label, name, value, focused, onChange, onFocus, onBlur }) {
  const isActive = focused || value;
  return (
    <div className="lk-float-field" style={{ marginBottom:14 }}>
      <label className={`lk-float-label textarea${isActive?" active":""}`}>{label}</label>
      <textarea
        value={value}
        onChange={e => onChange(name, e.target.value)}
        onFocus={() => onFocus(name)}
        onBlur={() => onBlur(name)}
        rows={4}
        style={{
          width:"100%", boxSizing:"border-box", resize:"vertical",
          background:"rgba(255,255,255,0.05)",
          border:`1px solid ${focused ? "rgba(6,182,212,0.6)" : "rgba(255,255,255,0.1)"}`,
          borderRadius:11, padding: isActive ? "22px 15px 10px" : "16px 15px",
          fontSize:14, color:"#fff", outline:"none",
          transition:"border-color 0.2s, padding 0.2s",
          fontFamily:"inherit",
        }}
      />
    </div>
  );
}

export default function ContactPage() {
  const isMobile = useIsMobile();
  const px = isMobile ? 16 : 40;

  const [values, setValues] = useState({ name:"", email:"", subject:"", message:"" });
  const [focused, setFocused] = useState({});
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");

  useEffect(() => {
    document.documentElement.classList.add("page-scrollable");
    return () => document.documentElement.classList.remove("page-scrollable");
  }, []);

  function handleChange(field, val) { setValues(v => ({...v, [field]:val})); }
  function handleFocus(field) { setFocused(f => ({...f, [field]:true})); }
  function handleBlur(field) { setFocused(f => ({...f, [field]:false})); }

  async function handleSubmit(e) {
    e.preventDefault();
    setSending(true);
    setSendError("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong");
      setSent(true);
    } catch (err) {
      setSendError(err.message || "Failed to send. Please try again.");
    } finally {
      setSending(false);
    }
  }

  return (
    <PageWrapper>
      <style>{CONTACT_CSS}</style>
      <DarkNav px={px} />

      {/* ── HERO BAR ── */}
      <div style={{
        padding:`${isMobile?24:52}px ${px}px ${isMobile?16:32}px`,
        borderBottom:"1px solid rgba(255,255,255,0.06)",
      }}>
        <div style={{
          display:"inline-flex", alignItems:"center", gap:6, marginBottom:16,
          background:"rgba(6,182,212,0.1)", border:"1px solid rgba(6,182,212,0.3)",
          borderRadius:99, padding:"4px 14px",
          fontSize:11, fontWeight:700, color:"#06B6D4", letterSpacing:1.3, textTransform:"uppercase",
        }}>
          Get in Touch
        </div>
        <h1 style={{
          fontSize:`clamp(28px,4.5vw,52px)`, fontWeight:900, margin:"0 0 12px", letterSpacing:-1.5, lineHeight:1.1,
          background:"linear-gradient(135deg,#fff 0%,#67E8F9 100%)",
          WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
        }}>
          We're here to help.
        </h1>
        <p style={{ fontSize:15, color:"rgba(255,255,255,0.5)", margin:0, maxWidth:440 }}>
          Reach out for support, billing, or partnership enquiries. Real humans respond.
        </p>
      </div>

      {/* ── TWO COLUMN LAYOUT ── */}
      <div style={{
        maxWidth:1100, margin:"0 auto",
        padding:`${isMobile?16:52}px ${px}px ${isMobile?40:100}px`,
        display:"grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1.15fr",
        gap: isMobile ? 20 : 48, alignItems:"start",
      }}>

        {/* ── LEFT: INFO ── */}
        <div className="contact-left">
          <p style={{ fontSize:14, color:"rgba(255,255,255,0.5)", lineHeight:1.85, marginBottom:32 }}>
            We're a small, focused team building India's leading FASTag-powered fleet intelligence platform. Response times are fast — usually within a few hours.
          </p>

          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            {[
              {
                icon:<Mail size={18} color="#06B6D4"/>, bg:"rgba(6,182,212,0.1)", border:"rgba(6,182,212,0.2)",
                title:"Email", sub:"We respond within 24 hours on business days",
                content:<a href="mailto:namaste@lorrykhojo.com" style={{fontSize:15,fontWeight:700,color:"#67E8F9",textDecoration:"none"}}
                  onMouseEnter={e=>e.currentTarget.style.color="#fff"} onMouseLeave={e=>e.currentTarget.style.color="#67E8F9"}
                >namaste@lorrykhojo.com</a>,
              },
              {
                icon:<Phone size={18} color="#34D399"/>, bg:"rgba(52,211,153,0.1)", border:"rgba(52,211,153,0.2)",
                title:"Phone", sub:"Mon–Sat, 10 AM – 6 PM IST",
                content:<a href="tel:+919711775065" style={{fontSize:15,fontWeight:700,color:"#34D399",textDecoration:"none"}}
                  onMouseEnter={e=>e.currentTarget.style.color="#fff"} onMouseLeave={e=>e.currentTarget.style.color="#34D399"}
                >+91 97117 75065</a>,
              },
              {
                icon:<MapPin size={18} color="#F87171"/>, bg:"rgba(248,113,113,0.1)", border:"rgba(248,113,113,0.2)",
                title:"Registered Office", sub:"Legal correspondence",
                content:<p style={{fontSize:13.5,color:"rgba(255,255,255,0.72)",margin:0,fontWeight:500,lineHeight:1.7}}>
                  Gupta Vibhor and Associates<br/>New Delhi, India
                </p>,
              },
              {
                icon:<Clock size={18} color="#FBBF24"/>, bg:"rgba(251,191,36,0.1)", border:"rgba(251,191,36,0.2)",
                title:"Support Hours", sub:"Our typical response window",
                content:<div style={{fontSize:13,color:"rgba(255,255,255,0.65)",lineHeight:2}}>
                  <div style={{display:"flex",justifyContent:"space-between",maxWidth:240}}>
                    <span>Monday – Friday</span><span style={{color:"#34D399",fontWeight:600}}>10 AM – 6 PM</span>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",maxWidth:240}}>
                    <span>Saturday</span><span style={{color:"#34D399",fontWeight:600}}>10 AM – 2 PM</span>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",maxWidth:240}}>
                    <span>Sunday</span><span style={{color:"#F87171",fontWeight:600}}>Closed</span>
                  </div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.28)",marginTop:4}}>IST (UTC+5:30)</div>
                </div>,
              },
            ].map((card,i) => (
              <div key={i} className="contact-card" style={{
                background:"rgba(10,14,40,0.65)",
                backdropFilter:"blur(16px)",
                border:`1px solid ${card.border}`,
                borderRadius:16, padding:"18px 20px",
              }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
                  <div style={{ width:36, height:36, borderRadius:10, background:card.bg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    {card.icon}
                  </div>
                  <div>
                    <div style={{ fontSize:13.5, fontWeight:700, color:"#fff" }}>{card.title}</div>
                    <div style={{ fontSize:11, color:"rgba(255,255,255,0.3)", marginTop:1 }}>{card.sub}</div>
                  </div>
                </div>
                {card.content}
              </div>
            ))}
          </div>

          {/* Legal entity */}
          <div style={{
            marginTop:20, padding:"16px 20px",
            background:"rgba(6,182,212,0.05)", border:"1px solid rgba(6,182,212,0.18)",
            borderLeft:"3px solid #06B6D4", borderRadius:"0 12px 12px 0",
          }}>
            <div style={{ fontSize:10, fontWeight:700, color:"#06B6D4", letterSpacing:1.2, textTransform:"uppercase", marginBottom:10 }}>Legal Entity</div>
            {[
              ["Business","Gupta Vibhor and Associates"],
              ["Brand","LorryKhojo"],
              ["Studio","GVA Labs"],
              ["Country","India"],
            ].map(([k,v]) => (
              <div key={k} style={{ display:"flex", gap:8, fontSize:12.5, marginBottom:5 }}>
                <span style={{ width:80, flexShrink:0, color:"rgba(6,182,212,0.7)" }}>{k}</span>
                <span style={{ color:"rgba(255,255,255,0.75)", fontWeight:500 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── RIGHT: FORM ── */}
        <div className="contact-right">
          <div style={{
            background:"rgba(10,14,40,0.72)", backdropFilter:"blur(24px)",
            border:"1px solid rgba(6,182,212,0.22)", borderRadius: isMobile ? 16 : 22,
            padding: isMobile ? "20px 16px" : "36px 36px",
            boxShadow:"0 8px 48px rgba(6,182,212,0.08), inset 0 1px 0 rgba(255,255,255,0.05)",
            animation:"cyanPulse 4s ease-in-out infinite",
          }}>
            {sent ? (
              <div style={{ textAlign:"center", padding:"40px 0", animation:"sentCheck 0.5s both" }}>
                <CheckCircle size={52} color="#06B6D4" style={{ marginBottom:16 }} />
                <h3 style={{ fontSize:20, fontWeight:800, color:"#fff", margin:"0 0 8px" }}>Message sent!</h3>
                <p style={{ fontSize:14, color:"rgba(255,255,255,0.5)", margin:"0 0 24px", lineHeight:1.7 }}>
                  We've received your message and will get back to you within 24 hours on business days.
                </p>
                <button onClick={() => setSent(false)} style={{
                  background:"rgba(6,182,212,0.15)", border:"1px solid rgba(6,182,212,0.4)",
                  borderRadius:10, padding:"10px 24px", color:"#67E8F9",
                  cursor:"pointer", fontSize:13, fontWeight:600,
                }}>Send another message</button>
              </div>
            ) : (
              <>
                <h2 style={{ fontSize:20, fontWeight:800, color:"#fff", margin:"0 0 6px" }}>Send us a message</h2>
                <p style={{ fontSize:13, color:"rgba(255,255,255,0.38)", margin:"0 0 28px" }}>
                  We read and respond to every message.
                </p>
                <form onSubmit={handleSubmit}>
                  <FloatInput label="Your name" name="name" value={values.name} focused={focused.name} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} />
                  <FloatInput label="Email address" name="email" type="email" value={values.email} focused={focused.email} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} />
                  <FloatInput label="Subject" name="subject" value={values.subject} focused={focused.subject} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} />
                  <FloatTextarea label="Message" name="message" value={values.message} focused={focused.message} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} />

                  {sendError && (
                    <div style={{ marginBottom:10, padding:"10px 14px", background:"rgba(248,113,113,0.1)", border:"1px solid rgba(248,113,113,0.3)", borderRadius:10, fontSize:13, color:"#F87171" }}>
                      {sendError}
                    </div>
                  )}
                  <div style={{ display:"flex", gap:12, marginTop:8, flexWrap:"wrap", alignItems:"center" }}>
                    <button
                      type="submit"
                      disabled={sending}
                      style={{
                        flex:1, minWidth:120, minHeight:48,
                        background: sending ? "rgba(6,182,212,0.3)" : "linear-gradient(135deg,#06B6D4,#0E7490)",
                        border:"none", borderRadius:12, padding:"14px 24px",
                        fontSize:14, fontWeight:700, color:"#fff",
                        cursor: sending ? "not-allowed" : "pointer",
                        display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                        boxShadow:"0 4px 20px rgba(6,182,212,0.35)",
                        transition:"all 0.18s",
                        opacity: sending ? 0.7 : 1,
                      }}
                      onMouseEnter={e => { if (!sending) { e.currentTarget.style.transform="scale(1.03)"; e.currentTarget.style.boxShadow="0 6px 28px rgba(6,182,212,0.5)"; }}}
                      onMouseLeave={e => { e.currentTarget.style.transform="scale(1)"; e.currentTarget.style.boxShadow="0 4px 20px rgba(6,182,212,0.35)"; }}
                    >
                      <Send size={15} /> {sending ? "Sending…" : "Send Message"}
                    </button>
                  </div>
                </form>

                {/* Quick help topics */}
                <div style={{ marginTop:28, paddingTop:24, borderTop:"1px solid rgba(255,255,255,0.06)" }}>
                  <div style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.3)", letterSpacing:1.2, textTransform:"uppercase", marginBottom:14 }}>Common topics</div>
                  <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                    {[
                      { topic:"Billing & Payments",   note:"Include your payment transaction ID" },
                      { topic:"Technical Support",    note:"Describe the issue with screenshots" },
                      { topic:"Refund Requests",      note:"Include registered email & payment ID" },
                      { topic:"Partnership Enquiries",note:"Tell us about your use case" },
                    ].map((item,i) => (
                      <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                        <span style={{ fontSize:12.5, fontWeight:600, color:"rgba(255,255,255,0.6)" }}>{item.topic}</span>
                        <span style={{ fontSize:11, color:"rgba(255,255,255,0.25)" }}>{item.note}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <DarkFooter px={px} />
    </PageWrapper>
  );
}
